import { useState } from 'react';

import type { LanguageProficiency } from '@/api/generated';
import { useContactFieldSuggestions } from '@/api/queries/contactFieldSuggestions';
import { withForm } from '@/components/form';
import {
  AddCustomField,
  EditableField,
  FieldGrid,
  SectionCard,
  type EditableFieldType,
} from '@/components/ui';

import { defaultFormValues, genderEnum } from '../../contactSchema';

interface CustomField {
  name: string;
  type: EditableFieldType;
  value: string;
}

const TITLE_BEFORE_OPTIONS = ['Dr.', 'Prof.', 'MUDr.', 'MDDr.', 'PharmDr.', 'Mgr.', 'Bc.'];
const TITLE_AFTER_OPTIONS = ['PhD', 'MBA', 'CSc.', 'DSc.', 'Ph.D.'];

export const ProfileSection = withForm({
  defaultValues: defaultFormValues,
  render: function ProfileSection({ form }) {
    // Custom fields are kept locally for now — the backend custom-fields API ships in Phase 7.
    const [customFields, setCustomFields] = useState<CustomField[]>([]);

    // Tenant-curated suggestions from contact.* counter tables; free text stays allowed.
    // When a table exists the field renders as a searchable combobox.
    const gender = useContactFieldSuggestions('profile.gender');
    const residence = useContactFieldSuggestions('profile.residence');
    const city = useContactFieldSuggestions('profile.city');
    const nationality = useContactFieldSuggestions('profile.nationality');
    const region = useContactFieldSuggestions('profile.region');
    const country = useContactFieldSuggestions('profile.country');
    const educationInstitution = useContactFieldSuggestions('profile.educationInstitution');
    const occupation = useContactFieldSuggestions('profile.occupation');
    const specialization = useContactFieldSuggestions('profile.specialization');
    const company = useContactFieldSuggestions('profile.company');
    const position = useContactFieldSuggestions('profile.position');

    const fieldCount = 17 + customFields.length;

    return (
      <SectionCard title="Profile information" count={`${fieldCount} fields`} dense>
        <FieldGrid>
          <form.AppField name="profile.titleBefore">
            {(field) => (
              <EditableField
                label="Title before"
                type="select"
                options={TITLE_BEFORE_OPTIONS.map((o) => ({ value: o, label: o }))}
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.firstName">
            {(field) => (
              <EditableField
                label="First name"
                value={field.state.value ?? ''}
                display={field.state.value || ''}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.lastName">
            {(field) => (
              <EditableField
                label="Last name"
                value={field.state.value ?? ''}
                display={field.state.value || ''}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.titleAfter">
            {(field) => (
              <EditableField
                label="Title after"
                type="select"
                options={TITLE_AFTER_OPTIONS.map((o) => ({ value: o, label: o }))}
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.middleName">
            {(field) => (
              <EditableField
                label="Middle name"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.dateOfBirth">
            {(field) => {
              const iso = field.state.value ? toIsoDate(field.state.value) : '';
              return (
                <EditableField
                  label="Date of birth"
                  type="date"
                  value={iso}
                  display={iso ? formatHumanDate(iso) : '—'}
                  onSave={(v) => field.handleChange(v ? new Date(v) : undefined)}
                />
              );
            }}
          </form.AppField>
          <form.AppField name="profile.gender">
            {(field) =>
              // With a contact.profile.gender counter the field becomes free text + curated
              // suggestions; without one, today's strict select stays.
              gender.hasTable ? (
                <EditableField
                  label="Gender"
                  value={field.state.value ?? ''}
                  display={field.state.value ? capitalize(field.state.value) : '—'}
                  suggestions={gender.suggestions}
                  searchable
                  onSave={(v) => field.handleChange(v)}
                />
              ) : (
                <EditableField
                  label="Gender"
                  type="select"
                  options={genderEnum.options
                    .filter((g) => g !== '')
                    .map((g) => ({ value: g, label: capitalize(g) }))}
                  value={field.state.value ?? ''}
                  display={field.state.value ? capitalize(field.state.value) : '—'}
                  onSave={(v) => field.handleChange(v)}
                />
              )
            }
          </form.AppField>
          <form.AppField name="profile.residence">
            {(field) => (
              <EditableField
                label="Residence"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={residence.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.city">
            {(field) => (
              <EditableField
                label="City"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={city.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.nationality">
            {(field) => (
              <EditableField
                label="Nationality"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={nationality.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.languages">
            {(field) => {
              const value = (field.state.value ?? []) as LanguageProficiency[];
              const display = value.map((l) => l.name).join(', ');
              return (
                <EditableField
                  label="Languages"
                  value={display}
                  display={display || '—'}
                  onSave={(v) =>
                    field.handleChange(
                      v
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map<LanguageProficiency>((name) => ({
                          name,
                          code: name.slice(0, 2).toLowerCase(),
                        })),
                    )
                  }
                />
              );
            }}
          </form.AppField>
          <form.AppField name="profile.bio">
            {(field) => (
              <EditableField
                label="Bio"
                type="textarea"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.interest">
            {(field) => (
              <EditableField
                label="Interest"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.occupation">
            {(field) => (
              <EditableField
                label="Occupation"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={occupation.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.position">
            {(field) => (
              <EditableField
                label="Position"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={position.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.specialization">
            {(field) => (
              <EditableField
                label="Specialization"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={specialization.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.company">
            {(field) => (
              <EditableField
                label="Company"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={company.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.educationInstitution">
            {(field) => (
              <EditableField
                label="Education institution"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={educationInstitution.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.region">
            {(field) => (
              <EditableField
                label="Region"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={region.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          <form.AppField name="profile.country">
            {(field) => (
              <EditableField
                label="Country"
                value={field.state.value ?? ''}
                display={field.state.value || '—'}
                suggestions={country.suggestions}
                searchable
                onSave={(v) => field.handleChange(v)}
              />
            )}
          </form.AppField>
          {customFields.map((cf) => (
            <EditableField
              key={cf.name}
              label={cf.name}
              type={cf.type}
              custom
              value={cf.value}
              display={cf.value || '—'}
              onSave={(v) =>
                setCustomFields((prev) =>
                  prev.map((f) => (f.name === cf.name ? { ...f, value: v } : f)),
                )
              }
            />
          ))}
        </FieldGrid>
        <AddCustomField
          section="Profile"
          onAdd={(name, type) => setCustomFields((prev) => [...prev, { name, type, value: '' }])}
        />
      </SectionCard>
    );
  },
});

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function toIsoDate(d: Date | string): string {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}

function formatHumanDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
